<template>
  <main class="main-content">
    <component :is="NavItem"></component>
  <div class="role-creation-page">
    <div class="form-container">
      <div class="section-header">
        <h2>Role Assignment</h2>
      </div>

      <div class="create-role-section">
        <h3>Create new role</h3>
        <p class="subtitle">Create the role and permissions to this user</p>

        <div class="form-row">
          <div class="form-group">
            <label for="roleName">Role Name <span class="required">*</span></label>
            <input
              type="text"
              id="roleName"
              v-model="roleName"
              placeholder="Super Admin Role"
              :class="{ 'error': error && !roleName.trim() }"
            />
          </div>

          <div class="form-group">
            <label for="description">Description</label>
            <input
              type="text"
              id="description"
              v-model="description"
              placeholder="This role is for an individual who has the right to manage others"
            />
          </div>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label class="checkbox-label">
              <input
                type="checkbox"
                v-model="roleActive"
              />
              Active Role
            </label>
          </div>

          <div class="form-group">
            <label class="checkbox-label">
              <input
                type="checkbox"
                v-model="roleDefault"
              />
              Default Role
            </label>
          </div>
        </div>

        <div v-if="error" class="error-message">
          {{ error }}
        </div>

        <div v-if="!roleCreated" class="form-actions" style="margin-top: 20px;">
          <button class="btn-cancel" @click="cancel" :disabled="isLoading">Cancel</button>
          <button class="btn-save" @click="saveRole" :disabled="isLoading">
            <span v-if="isLoading">Creating...</span>
            <span v-else>Save & Create</span>
          </button>
        </div>

        <div v-if="roleCreated" style="background: #e8f5e8; border: 2px solid #4caf50; padding: 20px; margin: 20px 0; text-align: center; border-radius: 8px;">
          <h3 style="color: #2e7d32; margin-bottom: 10px;">✓ Role Created Successfully!</h3>
          <p style="margin-bottom: 20px;">Role "{{ createdRole && createdRole.roleName }}" has been created.</p>
          <button
            @click="proceedToPermissions"
            :disabled="isLoading"
            style="background: #0d47a1; color: white; border: none; padding: 12px 24px; border-radius: 6px; cursor: pointer; font-size: 14px; font-weight: 500;"
          >
            <span v-if="isLoading">Loading Modules...</span>
            <span v-else>Assign Module Permissions</span>
          </button>
        </div>
      </div>

      <div class="permissions-section" v-if="showModulePermissions">
        <h3>Module Permissions</h3>
        <p class="subtitle">Assign module permissions to the role "{{ createdRole && createdRole.roleName }}"</p>

        <div class="modules-pagination-info" v-if="modules.length > 0" style="margin-bottom: 20px; color: #666; font-size: 14px;">
          Showing {{ ((currentPage - 1) * itemsPerPage) + 1 }}-{{ Math.min(currentPage * itemsPerPage, modules.length) }} of {{ modules.length }} modules
        </div>

        <div class="modules-grid">
          <div v-for="module in paginatedModules" :key="module.sysAreaId" class="module-card">
            <div class="module-header">
              <h4>{{ module.sysAreaName }}</h4>
              <p class="module-description">{{ module.sysAreaModule }}</p>
            </div>

            <div class="module-permissions">
              <div class="permission-row">
                <input
                  type="checkbox"
                  :id="`active-${module.sysAreaId}`"
                  v-model="modulePermissions[module.sysAreaId].roleAreaActive"
                />
                <label :for="`active-${module.sysAreaId}`">Active</label>
              </div>

              <div class="permission-row">
                <input
                  type="checkbox"
                  :id="`create-${module.sysAreaId}`"
                  v-model="modulePermissions[module.sysAreaId].roleAreaCreate"
                />
                <label :for="`create-${module.sysAreaId}`">Create</label>
              </div>

              <div class="permission-row">
                <input
                  type="checkbox"
                  :id="`read-${module.sysAreaId}`"
                  v-model="modulePermissions[module.sysAreaId].roleAreaRead"
                />
                <label :for="`read-${module.sysAreaId}`">Read</label>
              </div>

              <div class="permission-row">
                <input
                  type="checkbox"
                  :id="`update-${module.sysAreaId}`"
                  v-model="modulePermissions[module.sysAreaId].roleAreaUpdate"
                />
                <label :for="`update-${module.sysAreaId}`">Update</label>
              </div>

              <div class="permission-row">
                <input
                  type="checkbox"
                  :id="`delete-${module.sysAreaId}`"
                  v-model="modulePermissions[module.sysAreaId].roleAreaDelete"
                />
                <label :for="`delete-${module.sysAreaId}`">Delete</label>
              </div>
            </div>
          </div>
        </div>

        <div class="pagination-footer">
          <div class="pagination-controls">
            <span>Rows per page:</span>
            <select v-model="itemsPerPage">
              <option>10</option>
              <option>15</option>
              <option>20</option>
            </select>
            <span>{{ currentPage }} of {{ totalPages }}</span>
            <div class="pagination-buttons">
              <button @click="prevPage" :disabled="currentPage === 1">&lt;</button>
              <button @click="nextPage" :disabled="currentPage === totalPages">&gt;</button>
            </div>
          </div>

          <div class="action-buttons">
            <button class="btn-cancel" @click="cancel" :disabled="isLoading">Cancel</button>
            <button v-if="!showModulePermissions" class="btn-save" @click="saveRole" :disabled="isLoading">
              <span v-if="isLoading">Creating...</span>
              <span v-else>Save & Create</span>
            </button>
            <button v-else class="btn-save" @click="assignRoleModules" :disabled="isLoading">
              <span v-if="isLoading">Assigning Permissions...</span>
              <span v-else>Assign Permissions & Complete</span>
            </button>
          </div>
        </div>
      </div>
    </div>
    <role-success-create
      :visible="isSuccessModalVisible"
      @closeModal="closeSuccessModal"
      />
  </div>
  </main>
</template>

<script>
import axios from 'axios';
import NavItem from '@/admin/components/nav/NavItem.vue';
import RoleSuccessCreate from '@/admin/components/modals/RoleSuccessCreate.vue';

const CONTENT_TYPE_JSON = 'application/json';
const SUCCESS_PATTERNS = [
  'Role Created successfully',
  'successfully',
  'created',
  'Created',
];

export default {
  name: 'RegisterRole',
  components: { RoleSuccessCreate },
  computed: {
    NavItem() {
      return NavItem;
    },
    paginatedModules() {
      const startIndex = (this.currentPage - 1) * this.itemsPerPage;
      const endIndex = startIndex + this.itemsPerPage;
      return this.modules.slice(startIndex, endIndex);
    },
    totalPages() {
      return Math.ceil(this.modules.length / this.itemsPerPage);
    },
  },
  data() {
    const PERMISSION_A = 'Permission A';
    return {
      roleName: '',
      description: '',
      itemsPerPage: 10,
      currentPage: 1,
      memberPermissions: [
        { id: 'permA1', name: PERMISSION_A, checked: true },
        { id: 'permA2', name: PERMISSION_A, checked: false },
        { id: 'permA3', name: PERMISSION_A, checked: false },
        { id: 'permA4', name: PERMISSION_A, checked: false },
        { id: 'permA5', name: PERMISSION_A, checked: false },
      ],
      otherPermissions: [
        { id: 'permB1', name: PERMISSION_A, checked: true },
        { id: 'permB2', name: PERMISSION_A, checked: false },
        { id: 'permB3', name: PERMISSION_A, checked: false },
        { id: 'permB4', name: PERMISSION_A, checked: false },
        { id: 'permB5', name: PERMISSION_A, checked: false },
      ],
      memberPermissions2: [
        { id: 'permC1', name: PERMISSION_A, checked: false },
        { id: 'permC2', name: PERMISSION_A, checked: false },
        { id: 'permC3', name: PERMISSION_A, checked: false },
        { id: 'permC4', name: PERMISSION_A, checked: false },
        { id: 'permC5', name: PERMISSION_A, checked: false },
      ],
      otherPermissions2: [
        { id: 'permD1', name: PERMISSION_A, checked: false },
        { id: 'permD2', name: PERMISSION_A, checked: false },
        { id: 'permD3', name: PERMISSION_A, checked: false },
        { id: 'permD4', name: PERMISSION_A, checked: false },
        { id: 'permD5', name: PERMISSION_A, checked: false },
      ],
      isSuccessModalVisible: false,
      isLoading: false,
      error: null,
      roleActive: true,
      roleDefault: false,
      createdRole: null,
      modules: [],
      showModulePermissions: false,
      modulePermissions: {},
      roleCreated: false,
    };
  },
  methods: {
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage -= 1;
      }
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage += 1;
      }
    },
    cancel() {
      this.$emit('change-component', 'RoleManagement');
    },
    async saveRole() {
      if (!this.validateRoleName()) {
        return;
      }

      this.isLoading = true;
      this.error = null;

      try {
        const response = await this.createRoleApiCall();

        if (this.isSuccessfulResponse(response)) {
          this.handleSuccessfulRoleCreation(response);
        } else {
          this.error = 'Failed to create role. Please try again.';
        }
      } catch (error) {
        this.handleRoleCreationError(error);
      } finally {
        this.isLoading = false;
      }
    },

    validateRoleName() {
      if (!this.roleName.trim()) {
        this.error = 'Role name is required';
        return false;
      }
      return true;
    },

    async createRoleApiCall() {
      const payload = {
        roleId: 0,
        roleName: this.roleName.trim(),
        roleActive: this.roleActive,
        roleDefault: this.roleDefault,
      };

      return axios.post('/lmsagent/login/saveSystemRole', payload, {
        headers: {
          'Content-Type': CONTENT_TYPE_JSON,
          accept: '*/*',
        },
      });
    },

    isSuccessfulResponse(response) {
      return response.status === 200
        || response.status === 201
        || (response.data && typeof response.data === 'string' && SUCCESS_PATTERNS.some((pattern) => response.data.includes(pattern)))
        || (response.data && response.data.success === true);
    },

    extractRoleIdFromResponse(response) {
      if (response.data && typeof response.data === 'object' && response.data.roleId && response.data.roleId !== 0) {
        return response.data.roleId;
      }
      if (response.data && response.data.data && response.data.data.roleId && response.data.data.roleId !== 0) {
        return response.data.data.roleId;
      }
      if (response.data && typeof response.data === 'number') {
        return response.data;
      }
      if (response.data && typeof response.data === 'string') {
        const parsed = parseInt(response.data, 10);
        if (!Number.isNaN(parsed)) {
          return parsed;
        }
      }
      return null;
    },

    handleSuccessfulRoleCreation(response) {
      const extractedRoleId = this.extractRoleIdFromResponse(response);

      this.createdRole = {
        roleId: extractedRoleId,
        roleName: this.roleName.trim(),
        roleActive: this.roleActive,
        roleDefault: this.roleDefault,
      };

      this.roleCreated = true;
      this.scrollToSuccessMessage();
    },

    scrollToSuccessMessage() {
      this.$nextTick(() => {
        const successDiv = document.querySelector('div[style*="background: #e8f5e8"]');
        if (successDiv) {
          successDiv.scrollIntoView({ behavior: 'smooth' });
        }
      });
    },

    handleRoleCreationError(error) {
      if (error.response && error.response.data) {
        this.error = error.response.data.message || error.response.data || 'Failed to create role';
      } else if (error.request) {
        this.error = 'No response from server. Please check your connection.';
      } else {
        this.error = 'An error occurred while creating the role.';
      }
    },
    closeSuccessModal() {
      this.isSuccessModalVisible = false;
      this.$emit('change-component', 'RoleManagement');
    },

    async proceedToPermissions() {
      this.isLoading = true;
      this.error = null;

      try {
        await this.fetchModules();
        await new Promise((resolve) => setTimeout(resolve, 1000));
        await this.fetchCreatedRoleId();
        this.showModulePermissions = true;
        this.$nextTick(() => {
          const permissionsSection = document.querySelector('.permissions-section');
          if (permissionsSection) {
            permissionsSection.scrollIntoView({ behavior: 'smooth' });
          }
        });
      } catch (error) {
        this.$toast.error(`Error loading modules: ${error.message}`);
        this.error = 'Failed to load modules. Please try again.';
      } finally {
        this.isLoading = false;
      }
    },

    async fetchModules() {
      try {
        const response = await axios.get('/lmsagent/login/findSystemModules', {
          headers: {
            Authorization: `Bearer ${this.getAuthToken()}`,
            'Content-Type': CONTENT_TYPE_JSON,
          },
        });

        if (response.data && response.data.data) {
          this.modules = response.data.data;
          this.currentPage = 1;
          this.modules.forEach((module) => {
            this.modulePermissions[module.sysAreaId] = {
              roleAreaActive: false,
              roleAreaCreate: false,
              roleAreaUpdate: false,
              roleAreaDelete: false,
              roleAreaRead: false,
            };
          });
        } else {
          this.error = 'No modules found in response';
        }
      } catch (error) {
        this.error = `Failed to fetch modules: ${error.message}`;
      }
    },

    getAuthToken() {
      return localStorage.getItem('authToken') || sessionStorage.getItem('authToken') || 'dummy-token-if-not-set';
    },

    async assignRoleModules() {
      if (!this.createdRole || !this.createdRole.roleId) {
        this.error = 'Role ID is required to assign modules';
        return;
      }

      this.isLoading = true;
      this.error = null;

      try {
        const assignments = [];

        Object.keys(this.modulePermissions).forEach((moduleId) => {
          const permissions = this.modulePermissions[moduleId];
          if (Object.values(permissions).some((permission) => permission)) {
            assignments.push({
              moduleId: parseInt(moduleId, 10),
              roleId: this.createdRole.roleId,
              roleAreaActive: permissions.roleAreaActive,
              roleAreaCreate: permissions.roleAreaCreate,
              roleAreaUpdate: permissions.roleAreaUpdate,
              roleAreaDelete: permissions.roleAreaDelete,
              roleAreaRead: permissions.roleAreaRead,
            });
          }
        });

        if (assignments.length === 0) {
          this.error = 'Please assign at least one permission to proceed';
          return;
        }

        const response = await axios.post('/lmsagent/login/assignRoleModules', assignments, {
          headers: {
            'Content-Type': CONTENT_TYPE_JSON,
            accept: '*/*',
          },
        });

        if (response.data) {
          this.isSuccessModalVisible = true;
        } else {
          this.error = 'Failed to assign role modules. Please try again.';
        }
      } catch (error) {
        if (error.response && error.response.status === 500) {
          this.error = 'Server error: This role may already have module assignments, or there may be a database constraint. Please try creating a new role instead.';
        } else if (error.response && error.response.data) {
          this.error = error.response.data.message || error.response.data.error || error.response.data || 'Failed to assign role modules';
        } else if (error.request) {
          this.error = 'No response from server. Please check your connection.';
        } else {
          this.error = 'An error occurred while assigning role modules.';
        }
      } finally {
        this.isLoading = false;
      }
    },

    async fetchCreatedRoleId() {
      if (this.createdRole && this.createdRole.roleId) {
        this.$toast.success(`Role ID already available: ${this.createdRole.roleId}`);
        return;
      }

      try {
        const response = await axios.get('/lmsagent/login/findSystemRoles', {
          headers: {
            Authorization: `Bearer ${this.getAuthToken()}`,
            'Content-Type': CONTENT_TYPE_JSON,
          },
        });

        if (response.data && response.data.data) {
          const roles = response.data.data;

          const foundRole = roles.find((role) => role.roleName === this.createdRole.roleName);

          if (!foundRole) {
            this.error = `The role "${this.createdRole.roleName}" was created successfully, but it's not yet appearing in the roles list. This may be due to database replication delay. Please wait a moment and try clicking "Assign Module Permissions" again, or refresh the page.`;
            return;
          }

          if (foundRole) {
            this.createdRole.roleId = foundRole.roleId;
          } else {
            this.$toast.error('No matching role found');
            this.error = 'Could not find the created role ID. Please try refreshing and assigning permissions manually.';
          }
        }
      } catch (error) {
        this.error = `Failed to fetch role ID: ${error.message}`;
      }
    },
  },
};
</script>

<style scoped>
* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

.role-creation-page {
  max-width: 100%;
  margin: 0 auto;
  padding: 20px;
  background-color: white;
  border-radius: 15px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
}

.back-navigation {
  margin-bottom: 16px;
}

.icon-chevron-left::before {
  content: '←';
  margin-right: 8px;
}

.form-container {
  max-width: 100%;
  margin: 0 auto;
  padding: 20px;
  background-color: white;
  border-radius: 15px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
}

.section-header {
  padding: 16px 24px;
  border: 1px solid #e5e7eb;
}

.section-header h2 {
  font-size: 18px;
  font-weight: 500;
  color: #374151;
}

.create-role-section {
  padding: 24px;
  border-bottom: 1px solid #e5e7eb;
}

.create-role-section h3 {
  font-size: 18px;
  font-weight: 500;
  margin-bottom: 4px;
  color: #111827;
}

.subtitle {
  color: #6b7280;
  font-size: 14px;
  margin-bottom: 20px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
}

@media (max-width: 768px) {
  .form-row {
    grid-template-columns: 1fr;
  }
}

.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 8px;
  color: #374151;
}

.form-group input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.2s;
}

.form-group input:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.1);
}

.form-group input.error {
  border-color: #dc2626;
}

.required {
  color: #dc2626;
}

.checkbox-label {
  display: flex;
  align-items: center;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  cursor: pointer;
}

.checkbox-label input[type="checkbox"] {
  margin-right: 8px;
  width: 16px;
  height: 16px;
  accent-color: #2563eb;
}

.error-message {
  background-color: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 6px;
  padding: 12px;
  margin-top: 16px;
  color: #dc2626;
  font-size: 14px;
}

.success-message {
  background-color: #f0f9ff !important;
  border: 2px solid #0d47a1 !important;
  border-radius: 8px;
  padding: 24px;
  margin-top: 24px;
  text-align: center;
  border-left: 4px solid #0d47a1;
  width: 100%;
  display: block !important;
  visibility: visible !important;
  z-index: 1000;
}

.success-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background-color: #22c55e;
  color: white;
  font-size: 24px;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
}

.success-message h3 {
  color: #1e40af;
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 8px;
}

.success-message p {
  color: #374151;
  font-size: 14px;
  margin-bottom: 20px;
  line-height: 1.5;
}

.btn-proceed {
  background-color: #0d47a1 !important;
  color: white !important;
  border: none !important;
  border-radius: 6px;
  padding: 12px 24px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  display: inline-flex !important;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  visibility: visible !important;
  opacity: 1 !important;
}

.btn-proceed:hover:not(:disabled) {
  background-color: #1d4ed8;
}

.btn-proceed:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.permissions-section {
  padding: 24px;
}

.modules-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
  margin-bottom: 24px;
}

.module-card {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
  background-color: #fafafa;
}

.module-header {
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e5e7eb;
}

.module-header h4 {
  font-size: 16px;
  font-weight: 600;
  color: #111827;
  margin-bottom: 4px;
}

.module-description {
  font-size: 14px;
  color: #6b7280;
  margin: 0;
}

.module-permissions {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.permission-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.permission-row input[type="checkbox"] {
  width: 16px;
  height: 16px;
  accent-color: #2563eb;
}

.permission-row label {
  font-size: 14px;
  color: #374151;
  cursor: pointer;
}

.permissions-section h3 {
  font-size: 18px;
  font-weight: 500;
  margin-bottom: 4px;
  color: #111827;
}

.permissions-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 32px;
  margin-bottom: 32px;
}

@media (max-width: 768px) {
  .permissions-row {
    grid-template-columns: 1fr;
  }
}

.permissions-column h4 {
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 16px;
  color: #374151;
}

.permissions-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.permission-item {
  display: flex;
  align-items: center;
}

.permission-item input[type="checkbox"] {
  margin-right: 10px;
  width: 16px;
  height: 16px;
  accent-color: #2563eb;
}

.permission-item label {
  font-size: 14px;
  color: #4b5563;
}

.pagination-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid #e5e7eb;
  padding-top: 24px;
}

.pagination-controls {
  display: flex;
  align-items: center;
  font-size: 14px;
  color: #6b7280;
}

.pagination-controls select {
  margin: 0 8px;
  padding: 4px 8px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 14px;
}

.pagination-buttons {
  display: flex;
  margin-left: 12px;
}

.pagination-buttons button {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #d1d5db;
  background-color: white;
  border-radius: 4px;
  margin: 0 2px;
  cursor: pointer;
}

.pagination-buttons button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-buttons button:hover:not(:disabled) {
  background-color: #f3f4f6;
}

.action-buttons {
  display: flex;
  gap: 12px;
}

.btn-cancel {
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 500;
  background-color: white;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  color: #4b5563;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-save {
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 500;
  background-color: #2563eb;
  border: none;
  border-radius: 6px;
  color: white;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-cancel:hover {
  background-color: #f3f4f6;
}

.btn-save:hover:not(:disabled) {
  background-color: #1d4ed8;
}

.btn-cancel:disabled,
.btn-save:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.main-content {
  flex: 1;
  padding: 1.5rem 2rem;
  overflow-y: auto;
  background-color: #f0f2f5;
  left: 16%;
  margin-top: 4%;
  width: 100%;
}
</style>
